/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { Dialog } from '@headlessui/react';
import Alert from 'components/Alert';
import { useTranslation } from 'react-i18next';
import { CloseBlackIcon } from 'assets/icons';
import { Country } from 'utils';
import { Button } from 'components';
import { COUNTRIES } from '../../../../../constants';
import Visa from '../../../../../assets/images/visa.jpg';
import Mastercard from '../../../../../assets/images/mastercard.jpg';

interface AddCardModalFlowFormProps {
  nameOnCard: string;
  country: Country;
  makeCardDefault: boolean;
  className?: string;
  error: Error | null;
  onClose: () => void;
  onNameOnCardChanged: (newValue: string) => void;
  onCountryChanged: (newValue: Country) => void;
  onMakeCardDefaultChanged: (newValue: boolean) => void;
  onSaveCardButtonClicked: () => void;
  isProcessing?: boolean;
  /** Phase gate: false = collecting details (Continue), true = card mounted (Save). */
  detailsConfirmed: boolean;
  canContinue: boolean;
  onContinue: () => void;
  payDisabled: boolean;
  /** Mount point for the checkout.com Flow SDK card fields. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** True once the Flow card component is mounted and ready. */
  flowReady?: boolean;
  /** True if the Flow session / SDK failed to initialise. */
  flowError?: boolean;
}

/**
 * checkout.com FULL FLOW variant of `AddCardModalForm`. Two-phase: phase 1 collects the cardholder name
 * + billing country (which the session is created with on "Continue"); phase 2 mounts the SDK card
 * fields into `containerRef` and the "Save" button drives `submit()`.
 */
export default function AddCardModalFlowForm({
  nameOnCard,
  country,
  makeCardDefault,
  onNameOnCardChanged,
  onCountryChanged,
  onMakeCardDefaultChanged,
  onSaveCardButtonClicked,
  onClose,
  error,
  className = '',
  isProcessing,
  detailsConfirmed,
  canContinue,
  onContinue,
  payDisabled,
  containerRef,
  flowReady,
  flowError,
}: AddCardModalFlowFormProps) {
  const { t } = useTranslation();
  const buttonClass = (disabled: boolean) =>
    `w-full mt-6 rounded-xl px-6 py-3 font-medium transition-all flex items-center justify-center gap-2 ${
      disabled
        ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
        : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
    }`;
  return (
    <Dialog.Panel
      className={`w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 p-6 text-left align-middle shadow-xl dark:shadow-slate-900/50 transition-all border border-gray-100 dark:border-slate-700 ${className}`}
    >
      {/* Header */}
      <Dialog.Title
        as="div"
        className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-slate-700"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange/20 to-orange/10 dark:from-teal-500/20 dark:to-teal-500/10 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-orange dark:text-teal-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('account:payments.addCard.title')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('account:payments.addCard.subtitle', 'Securely add your payment card')}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          <CloseBlackIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
      </Dialog.Title>

      {/* Accepted Cards */}
      <div className="flex items-center justify-between mt-4 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
        <span className="text-sm text-gray-600 dark:text-gray-300">{t('account:payments.addCard.acceptedCards')}</span>
        <div className="flex gap-2">
          <img src={Visa} alt="visa" className="h-6 rounded" />
          <img src={Mastercard} alt="mastercard" className="h-6 rounded" />
        </div>
      </div>

      {/* Info/Error Message */}
      <div className="mt-4">
        {error || flowError ? (
          <Alert className="bg-red w-full text-sm text-white m-0 rounded-xl" show>
            {t('common:errorResult.title')}
          </Alert>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('account:payments.addCard.explainerOptional')}</p>
        )}
      </div>

      {/* Form Fields */}
      <div className="mt-5">
        <div className="flex flex-col gap-3">
          {/* Name on Card */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              {t('account:payments.addCard.formFields.labels.name', 'Name on Card')}
            </label>
            <input
              id="name"
              className="checkout-com-text-field w-full rounded-lg"
              type="text"
              disabled={detailsConfirmed}
              placeholder={t('account:payments.addCard.formFields.placeholders.name')}
              value={nameOnCard}
              onChange={(e) => onNameOnCardChanged(e.target.value)}
            />
          </div>

          {/* Country */}
          <div>
            <label htmlFor="country" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              {t('account:payments.addCard.formFields.placeholders.country')}
            </label>
            <select
              id="country"
              disabled={detailsConfirmed}
              className="w-full text-sm outline-none bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-3 focus:border-orange dark:focus:border-teal-500 transition-colors disabled:opacity-60"
              value={country}
              onChange={({ target: { value } }) => onCountryChanged(value)}
            >
              {COUNTRIES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Make Default Checkbox */}
          <label
            htmlFor="makedefault"
            className="flex items-center gap-3 mt-2 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl cursor-pointer"
          >
            <input
              id="makedefault"
              type="checkbox"
              checked={makeCardDefault}
              onChange={() => onMakeCardDefaultChanged(!makeCardDefault)}
              disabled
              className="appearance-none w-4 h-4 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-orange dark:checked:bg-teal-500 checked:border-orange dark:checked:border-teal-500 focus:ring-2 focus:ring-orange/30 dark:focus:ring-teal-500/30 focus:ring-offset-0 disabled:opacity-50 transition-all cursor-pointer"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('account:payments.addCard.formFields.makeDefault')}
            </span>
          </label>

          {/* Card fields (rendered by the checkout.com Flow SDK) — phase 2 only */}
          {detailsConfirmed && (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                {t('account:payments.addCard.formFields.labels.cardNumber', 'Card Number')}
              </label>
              <div ref={containerRef} data-testid="flow-card-container" />
              {!flowReady && !flowError && (
                <span className="text-gray-400 text-xs mt-1 block">{t('common:loading', 'Loading…')}</span>
              )}
            </div>
          )}
        </div>

        {/* Phase 1 → "Continue" creates the session; phase 2 → "Save" submits via the SDK. */}
        {!detailsConfirmed ? (
          <Button isDisable={!canContinue} type="button" className={buttonClass(!canContinue)} onClick={onContinue}>
            {t('account:payments.addCard.actions.continue', 'Continue')}
          </Button>
        ) : (
          <Button
            isDisable={payDisabled}
            isProcessing={isProcessing}
            type="button"
            className={buttonClass(payDisabled)}
            onClick={onSaveCardButtonClicked}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            {t('account:payments.addCard.actions.save')}
          </Button>
        )}
      </div>
    </Dialog.Panel>
  );
}
